<template>
  <Report
    :report-id="reportId"
    title="Engagement Trend"
    tooltip-text="A trend showing the proportion of users who started a flow and subsequently engaged and completed the flow"
    class="chart-container"
    :dataEmpty="isDataEmpty"
    :loadedData="isDataLoaded"
    :loadingError="loadingError"
  >
    <template #header-addons>
      <span class="button" :class="{ 'is-static': showLineChart }" @click="switchLineChart()">
        <font-awesome-icon icon="chart-line"></font-awesome-icon>
      </span>
      <span
        class="button percent-button"
        :class="{ 'is-static': showPercentageBarChart }"
        @click="switchStackedChart()"
      >
        <font-awesome-icon icon="percent"></font-awesome-icon>
      </span>
      <span class="button" :class="{ 'is-static': showBarChart }" @click="switchBarChart()">
        <font-awesome-icon icon="chart-bar"></font-awesome-icon>
      </span>
    </template>

    <template #chart>
      <LineChart
        :reportId="reportId"
        class="chart"
        v-if="showLineChart"
        :data="data"
        :fill="true"
        :seriesOrder="['Started', 'Engaged', 'Completed']"
      />
      <PercentageStackedBarChart
        :reportId="reportId"
        class="chart"
        v-if="showPercentageBarChart"
        :data="data"
        :seriesOrder="['Started', 'Engaged', 'Completed']"
        series-to-compare-to="Started"
      />
      <BarChart
        :reportId="reportId"
        class="chart"
        v-if="showBarChart"
        :data="data"
        :seriesOrder="['Started', 'Engaged', 'Completed']"
      />
    </template>
  </Report>
</template>

<script lang="ts" setup>
import PercentageStackedBarChart from '../chartjs/PercentageStackedBarChart.vue';
import BarChart from '../chartjs/BarChart.vue';
import LineChart from '../chartjs/LineChart.vue';
import { ReportType } from '../../models/reportType';
import { useFlowReport } from '@/features/reports/components/flows/useFlowReport';
import { watch } from 'vue';
import Report from '@/features/reports/components/Report.vue';

const emit = defineEmits<{
  (e: 'loaded'): void;
}>();

const {
  reportId,
  isDataLoaded,
  isDataEmpty,
  loadingError,
  data,
  showLineChart,
  showPercentageBarChart,
  showBarChart,
  switchLineChart,
  switchStackedChart,
  switchBarChart,
} = useFlowReport(ReportType.FLOW_ENGAGEMENT);

watch([isDataLoaded, loadingError], () => {
  if (isDataLoaded.value) emit('loaded');
});
</script>

<style scoped lang="scss">
@use '@/features/reports/components/reports.scss' as *;
</style>
